"use client";

import { FilmIcon, FireIcon } from "@heroicons/react/24/solid";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { trackEvent } from "../analytics-wrapper";
import { webAssetsCdnBaseUrl } from "../constants";
import { formatNumber } from "../formatters";
import { Username } from "../username";
import { SocialShare } from "./social-share";
import { TemplateRecommendations } from "./template-recommendations";
import { TemplateVideo } from "./template-video";
import { SingleTemplateData } from "./templates-action";

export function TemplateDetails({
  template,
  showSimilarTemplates = false,
}: {
  template: SingleTemplateData;
  /**
   * whether to show templates that are similar. This is only used for the template details page when it is not shown on the modal.
   * Also used to remove autoPlay since the user won't have interacted with the page, and browser blocks the auto play.
   */
  showSimilarTemplates?: boolean;
}) {
  const router = useRouter();
  const { data: session } = useSession();

  return (
    <div className="grid h-screen grid-cols-1 gap-4 overflow-y-auto overflow-x-hidden rounded-2xl bg-slate-900 p-4 pt-8 transition-all sm:h-fit md:grid-cols-2 md:gap-8 md:p-8 lg:grid-cols-3">
      <TemplateVideo
        controls
        showCompressed={false}
        videoId={`${template.id}`}
        preload="auto"
        playsInline
        autoPlay={!showSimilarTemplates}
        className="min-h-60 h-[320px] w-full rounded-xl bg-slate-950/50 md:h-[500px] lg:col-span-2"
      />

      <div className="flex flex-col gap-6 rounded-lg border-2 border-slate-800 p-4">
        <h3 className="text-2xl font-bold">{template.name}</h3>
        <div className="w-fit">
          <Username
            username={template.author || "Anonymous"}
            verifiedAccount={false}
            profilePictureUrl={`${webAssetsCdnBaseUrl}/magic-hour-profile-picture.png`}
          />
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <p className="text-sm capitalize text-gray-100">
            {template.type.replaceAll("_", "-").toLowerCase()}
          </p>

          <div className="flex items-center gap-1 rounded-lg text-xs">
            <FilmIcon className="h-4 w-4 text-white" />1 clip
          </div>

          <div className="flex items-center gap-1 rounded-lg text-xs">
            <FireIcon className="h-4 w-4 text-white" />
            {formatNumber(template.usedCount)} videos
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => {
            trackEvent({
              eventName: "createWithTemplate",
              action: "click",
              category: template.id,
            });

            const createLink = `/create/beta?template=${template.id}`;

            const link = session
              ? createLink
              : `/sign-in?callbackUrl=${encodeURIComponent(createLink)}`;

            router.push(link);
          }}
        >
          Use this template
        </button>

        <SocialShare template={template} />

        <p className="flex items-center gap-2 text-sm text-gray-300">
          {/* https://www.svgrepo.com/svg/344439/aspect-ratio */}
          <svg
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
            fill="white"
            className="bi bi-aspect-ratio h-6 w-6"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path d="M0 3.5A1.5 1.5 0 0 1 1.5 2h13A1.5 1.5 0 0 1 16 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 12.5v-9zM1.5 3a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z"></path>{" "}
              <path d="M2 4.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1H3v2.5a.5.5 0 0 1-1 0v-3zm12 7a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1 0-1H13V8.5a.5.5 0 0 1 1 0v3z"></path>{" "}
            </g>
          </svg>
          <strong>Any aspect ratio</strong>
        </p>
        {template.artStyle && (
          <p className="flex items-center gap-2 text-sm text-gray-300">
            {/* https://www.svgrepo.com/svg/392445/art-design-paint-pallet-format-text */}
            <svg
              viewBox="0 0 20 20"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              fill="white"
              className="h-6 w-6"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <g id="layer1">
                  <path d="M 10,2 C 4.5399633,2 0,5.5155856 0,10 a 0.50005,0.50005 0 0 0 0,0.002 c 0.00211683,0.498935 0.0623158,0.997281 0.1796875,1.486328 a 0.50005,0.50005 0 0 0 0.65625,0.353516 c 0.166982,-0.06005 0.3346003,-0.11455 0.5058594,-0.162109 3.2465843,-0.86948 6.3865589,0.651858 7.0898437,3.277343 0.1999132,0.753403 0.1847938,1.55275 -0.046875,2.341797 A 0.50005,0.50005 0 0 0 8.8144531,17.9375 C 9.2066201,17.97715 9.6008792,17.99763 9.9960938,18 A 0.50005,0.50005 0 0 0 10,18 C 15.460037,18 20,14.484414 20,10 20,5.5155856 15.460037,2 10,2 Z m 0,1 c 5.033373,0 9,3.2001431 9,7 0,3.798831 -3.964655,6.998273 -8.996094,7 C 9.7877816,16.9987 9.5727572,16.978264 9.3574219,16.964844 9.490202,16.205055 9.5915438,15.434329 9.3964844,14.699219 8.5282549,11.457966 4.7749194,9.7293469 1.0878906,10.714844 1.0549598,10.475808 1.0010202,10.238508 1,9.9980469 1.0013454,6.1990381 4.9674803,3 10,3 Z M 9.5,4 C 8.677494,4 8,4.677495 8,5.5 8,6.322505 8.677494,7 9.5,7 10.322505,7 11,6.322505 11,5.5 11,4.677495 10.322505,4 9.5,4 Z m -4,1 C 4.677495,5 4,5.677495 4,6.5 4,7.322505 4.677495,8 5.5,8 6.322505,8 7,7.322505 7,6.5 7,5.677495 6.322505,5 5.5,5 Z m 4,0 C 9.782065,5 10,5.217935 10,5.5 10,5.782065 9.782065,6 9.5,6 9.217935,6 9,5.782065 9,5.5 9,5.217935 9.217935,5 9.5,5 Z m 4,0 C 12.677494,5 12,5.677495 12,6.5 12,7.322505 12.677494,8 13.5,8 14.322505,8 15,7.322505 15,6.5 15,5.677495 14.322505,5 13.5,5 Z m -8,1 C 5.782065,6 6,6.217935 6,6.5 6,6.782065 5.782065,7 5.5,7 5.217935,7 5,6.782065 5,6.5 5,6.217935 5.217935,6 5.5,6 Z m 8,0 C 13.782065,6 14,6.217935 14,6.5 14,6.782065 13.782065,7 13.5,7 13.217935,7 13,6.782065 13,6.5 13,6.217935 13.217935,6 13.5,6 Z m 3,2 C 15.677494,8 15,8.677495 15,9.5 15,10.322505 15.677494,11 16.5,11 17.322505,11 18,10.322505 18,9.5 18,8.677495 17.322505,8 16.5,8 Z m 0,1 C 16.782065,9 17,9.217935 17,9.5 17,9.782065 16.782065,10 16.5,10 16.217935,10 16,9.782065 16,9.5 16,9.217935 16.217935,9 16.5,9 Z m -3,3 C 12.677494,12 12,12.677495 12,13.5 12,14.322505 12.677494,15 13.5,15 14.322505,15 15,14.322505 15,13.5 15,12.677495 14.322505,12 13.5,12 Z m 0,1 C 13.782065,13 14,13.217935 14,13.5 14,13.782065 13.782065,14 13.5,14 13.217935,14 13,13.782065 13,13.5 13,13.217935 13.217935,13 13.5,13 Z"></path>
                </g>
              </g>
            </svg>
            <strong>{template.artStyle} Art Style</strong>
          </p>
        )}
        {template.cameraEffect && (
          <p className="flex items-center gap-2 text-sm text-gray-300">
            {/* https://www.svgrepo.com/svg/533060/camera-alt */}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="white"
              className="h-6 w-6"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <path
                  d="M3 4H8M3 11H9.76389M14.2361 11H21M21 7.2V16.8C21 17.9201 21 18.4802 20.782 18.908C20.5903 19.2843 20.2843 19.5903 19.908 19.782C19.4802 20 18.9201 20 17.8 20H6.2C5.0799 20 4.51984 20 4.09202 19.782C3.71569 19.5903 3.40973 19.2843 3.21799 18.908C3 18.4802 3 17.9201 3 16.8V10.2C3 9.0799 3 8.51984 3.21799 8.09202C3.40973 7.71569 3.71569 7.40973 4.09202 7.21799C4.51984 7 5.0799 7 6.2 7H7.67452C8.1637 7 8.40829 7 8.63846 6.94474C8.84254 6.89575 9.03763 6.81494 9.21657 6.70528C9.4184 6.5816 9.59135 6.40865 9.93726 6.06274L11.0627 4.93726C11.4086 4.59136 11.5816 4.4184 11.7834 4.29472C11.9624 4.18506 12.1575 4.10425 12.3615 4.05526C12.5917 4 12.8363 4 13.3255 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2ZM15 13C15 14.6569 13.6569 16 12 16C10.3431 16 9 14.6569 9 13C9 11.3431 10.3431 10 12 10C13.6569 10 15 11.3431 15 13Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </g>
            </svg>
            <strong>{template.cameraEffect} Camera Effect</strong>
          </p>
        )}
        {template.prompt && (
          <div>
            <div
              className="flex items-center gap-2 text-sm text-gray-300"
              style={{ overflowWrap: "anywhere" }}
            >
              {/* https://www.svgrepo.com/svg/532166/document-layout-left */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    d="M3 15H21M3 19H13M21 7H13M21 11H13M4.6 11H7.4C7.96005 11 8.24008 11 8.45399 10.891C8.64215 10.7951 8.79513 10.6422 8.89101 10.454C9 10.2401 9 9.96005 9 9.4V6.6C9 6.03995 9 5.75992 8.89101 5.54601C8.79513 5.35785 8.64215 5.20487 8.45399 5.10899C8.24008 5 7.96005 5 7.4 5H4.6C4.03995 5 3.75992 5 3.54601 5.10899C3.35785 5.20487 3.20487 5.35785 3.10899 5.54601C3 5.75992 3 6.03995 3 6.6V9.4C3 9.96005 3 10.2401 3.10899 10.454C3.20487 10.6422 3.35785 10.7951 3.54601 10.891C3.75992 11 4.03995 11 4.6 11Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </g>
              </svg>
              <strong>Prompt</strong>
            </div>
            <p className="max-h-24 overflow-y-scroll rounded-md bg-black/20 p-4">
              {template.prompt?.replaceAll("_", " ")}
            </p>
          </div>
        )}

        {template.tags && (
          <div>
            <p className="flex items-center gap-2 text-sm text-gray-300">
              {/* https://www.svgrepo.com/svg/392445/art-design-paint-pallet-format-text */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path
                    d="M7.0498 7.0498H7.0598M10.5118 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V10.5118C3 11.2455 3 11.6124 3.08289 11.9577C3.15638 12.2638 3.27759 12.5564 3.44208 12.8249C3.6276 13.1276 3.88703 13.387 4.40589 13.9059L9.10589 18.6059C10.2939 19.7939 10.888 20.388 11.5729 20.6105C12.1755 20.8063 12.8245 20.8063 13.4271 20.6105C14.112 20.388 14.7061 19.7939 15.8941 18.6059L18.6059 15.8941C19.7939 14.7061 20.388 14.112 20.6105 13.4271C20.8063 12.8245 20.8063 12.1755 20.6105 11.5729C20.388 10.888 19.7939 10.2939 18.6059 9.10589L13.9059 4.40589C13.387 3.88703 13.1276 3.6276 12.8249 3.44208C12.5564 3.27759 12.2638 3.15638 11.9577 3.08289C11.6124 3 11.2455 3 10.5118 3ZM7.5498 7.0498C7.5498 7.32595 7.32595 7.5498 7.0498 7.5498C6.77366 7.5498 6.5498 7.32595 6.5498 7.0498C6.5498 6.77366 6.77366 6.5498 7.0498 6.5498C7.32595 6.5498 7.5498 6.77366 7.5498 7.0498Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </g>
              </svg>
              <strong>Tags</strong>
            </p>
            {template.tags?.split(",").map((tag) => (
              <div className="badge badge-neutral mt-2 capitalize" key={tag}>
                {tag.toLowerCase()}
              </div>
            ))}
          </div>
        )}
      </div>

      {template.templateContent && (
        <>
          <div
            className="prose col-span-full mx-auto w-full max-w-(--breakpoint-xl) text-white prose-h1:hidden"
            dangerouslySetInnerHTML={{ __html: template.templateContent }}
          ></div>
          <button
            className="btn btn-primary col-span-full mx-auto w-full max-w-(--breakpoint-md)"
            onClick={() => {
              trackEvent({
                eventName: "createWithTemplate",
                action: "click",
                category: template.id,
              });

              const createLink = `/create/beta?template=${template.id}`;

              const link = !!session
                ? createLink
                : `/sign-in?callbackUrl=${encodeURIComponent(createLink)}`;

              router.push(link);
            }}
          >
            Use this template
          </button>
        </>
      )}

      {showSimilarTemplates ? (
        <div className="col-span-full">
          <TemplateRecommendations template={template} />
        </div>
      ) : (
        <div className="h-24 sm:h-0"></div>
      )}
    </div>
  );
}
